import axios from "axios";

// Same-origin routing: when the app is served from a different https host than the configured backend
// (e.g. the custom domain alongside the original production URL), call that host's own /api so the
// httpOnly session cookie, SSE feeds and download links keep working on every domain.
const configured = process.env.REACT_APP_BACKEND_URL;
const sameOrigin = (() => {
  try {
    const cfg = new URL(configured);
    return window.location.protocol === "https:" && window.location.host !== cfg.host;
  } catch { return false; }
})();
export const BACKEND_URL = sameOrigin ? window.location.origin : configured;
export const API_BASE = `${BACKEND_URL}/api`;

import { nativePlatform } from "@/lib/platform";

export const api = axios.create({ baseURL: API_BASE, withCredentials: true });
api.interceptors.request.use((cfg) => { const p = nativePlatform(); if (p) cfg.headers["X-Apex-Platform"] = p; return cfg; });

// Identical GETs fired at the same moment (e.g. /watchlist from two components) share one request.
const inflight = new Map();
const rawGet = api.get.bind(api);
api.get = (url, cfg) => {
  const key = `${url}?${JSON.stringify(cfg?.params || {})}`;
  if (inflight.has(key)) return inflight.get(key);
  const p = rawGet(url, cfg).finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
};

// Seconds the server asked us to wait (Retry-After header or `detail.retry_after`); 0 when none.
export const retryAfterOf = (err) => {
  const h = Number(err?.response?.headers?.["retry-after"]);
  const d = Number(err?.response?.data?.detail?.retry_after);
  return Math.max(0, Math.min(180, isFinite(h) && h > 0 ? h : isFinite(d) && d > 0 ? d : 0));
};

export const apiError = (err, fallback = "Something went wrong") => {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (typeof e?.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return typeof d?.msg === "string" ? d.msg : typeof d?.message === "string" ? d.message : fallback;
};

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const cfg = err?.config || {};
    // Silent retry on transient 503 for GET reads — up to 2 attempts — but never inside a provider back-off window:
    // when the server says Retry-After, callers get the rejection at once (with err.retryAfter) and decide what to keep showing.
    const delayed = err?.response?.data?.detail?.code === "data_delayed";
    if (status === 503 && !delayed && (cfg.method || "get").toLowerCase() === "get") {
      const ra = retryAfterOf(err);
      err.retryAfter = ra;
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      if (ra === 0 && cfg.__retryCount <= 2) {
        await new Promise((r) => setTimeout(r, 1200 * cfg.__retryCount));
        return api.request(cfg);
      }
    }
    // Paid feature or quota → let the layout show an upgrade toast; callers still receive the rejection.
    if (status === 402) {
      const d = err?.response?.data?.detail;
      window.dispatchEvent(new CustomEvent("apx:upgrade-required", { detail: typeof d === "object" ? d : { message: d } }));
    }
    // Only auto-logout on 401 from the /me probe (session cookie expired or cleared).
    if (status === 401 && /\/auth\/me/.test(url)) {
      const path = window.location.pathname;
      if (!path.startsWith("/login") && !path.startsWith("/signup") && !["/delete-account", "/privacy", "/about", "/contact", "/partners", "/terms", "/risk-disclosure", "/install", "/transparency", "/glossary", "/coinbase-listed", "/markets", "/feed-health", "/pricing"].some((x) => path.startsWith(x)) && path !== "/") {
        window.location.href = `/login?next=${encodeURIComponent(path + window.location.search)}`;
      }
    }
    return Promise.reject(err);
  }
);

export const fmtUsd = (n, opts = {}) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const abs = Math.abs(n);
  const digits = opts.digits ?? (abs >= 1 ? 2 : abs >= 0.01 ? 4 : 6);
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: digits,
    minimumFractionDigits: Math.min(digits, 2),
  }).format(n);
};

export const fmtPct = (n, d = 2) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${Number(n).toFixed(d)}%`;
};

export const fmtNum = (n, digits = 2) => {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e12) return (n / 1e12).toFixed(2) + "T";
  if (abs >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (abs >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (abs >= 1e3) return (n / 1e3).toFixed(2) + "K";
  return Number(n).toFixed(digits);
};

// Only same-app relative paths are accepted as post-login destinations.
export const safeNext = (raw) => (raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : "/dashboard");
